import type { HashAlgorithm, HashResult } from '../types';

// Generate a cryptographically secure random salt
export function generateSalt(length: number = 16): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

// Generate a unique ID
export function generateId(): string {
  return `${Date.now()}-${generateSalt(8)}`;
}

// Hash data using Web Crypto API
export async function hashData(
  data: string,
  algorithm: HashAlgorithm = 'SHA-256',
  salt?: string
): Promise<HashResult> {
  const encoder = new TextEncoder();
  const dataToHash = salt ? `${salt}:${data}` : data;
  const dataBuffer = encoder.encode(dataToHash);

  const hashBuffer = await crypto.subtle.digest(algorithm, dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return {
    hash,
    algorithm,
    timestamp: new Date().toISOString(),
    inputSize: data.length,
    salt
  };
}

// Verify data against a known hash
export async function verifyHash(
  data: string,
  expectedHash: string,
  algorithm: HashAlgorithm = 'SHA-256',
  salt?: string
): Promise<boolean> {
  const result = await hashData(data, algorithm, salt);
  return result.hash === expectedHash;
}

// Hash a file using Web Crypto API
export async function hashFile(
  file: File,
  algorithm: HashAlgorithm = 'SHA-256',
  onProgress?: (progress: number) => void
): Promise<HashResult> {
  const chunkSize = 1024 * 1024; // 1MB chunks
  const chunks = Math.ceil(file.size / chunkSize);
  const hashParts: Uint8Array[] = [];

  for (let i = 0; i < chunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    const arrayBuffer = await chunk.arrayBuffer();
    hashParts.push(new Uint8Array(arrayBuffer));

    if (onProgress) {
      onProgress((i + 1) / chunks * 100);
    }
  }

  // Combine all chunks
  const totalLength = hashParts.reduce((acc, arr) => acc + arr.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of hashParts) {
    combined.set(part, offset);
    offset += part.length;
  }

  const hashBuffer = await crypto.subtle.digest(algorithm, combined);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return {
    hash,
    algorithm,
    timestamp: new Date().toISOString(),
    inputSize: file.size
  };
}

// Format hash for display (truncated with ellipsis)
export function formatHash(hash: string, length: number = 16): string {
  if (hash.length <= length * 2) return hash;
  return `${hash.slice(0, length)}...${hash.slice(-length)}`;
}

// Get hash algorithm info
export function getAlgorithmInfo(algorithm: HashAlgorithm): { bits: number; description: string } {
  const info: Record<HashAlgorithm, { bits: number; description: string }> = {
    'SHA-256': {
      bits: 256,
      description: 'Most widely used, excellent balance of security and speed'
    },
    'SHA-384': {
      bits: 384,
      description: 'Truncated version of SHA-512, higher security margin'
    },
    'SHA-512': {
      bits: 512,
      description: 'Maximum security, larger output size'
    }
  };
  return info[algorithm];
}

// Calculate collision resistance (2^(bits/2) for birthday attack)
export function getCollisionResistance(algorithm: HashAlgorithm): string {
  const bits = getAlgorithmInfo(algorithm).bits;
  return `2^${bits / 2}`;
}
